import { useEffect, useId, useMemo, useState } from "react";

import {
  defaultPhoto, describePhoto, getSubjectPhotoOptions, photoKey, STREETVIEW_UNCREDITED, streetViewFallback,
  type SubjectPhotoOption, type SubjectPhotoOptions,
} from "./subjectPhotos";

/**
 * Loads the photo options for a job. Lives in the flow (not in the step) so the answer and the
 * agent's choice survive moving between steps 1 to 4 and into the finished report. `null` is
 * "still loading"; no job id means there is nothing to ask about yet.
 */
export function useSubjectPhotoOptions(jobId: string | null | undefined, address?: string | null): {
  options: SubjectPhotoOptions | null;
  retry: () => void;
} {
  const [raw, setOptions] = useState<SubjectPhotoOptions | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!jobId) { setOptions(null); return; }
    let live = true;
    setOptions(null);
    void getSubjectPhotoOptions(jobId).then((o) => { if (live) setOptions(o); });
    return () => { live = false; };
  }, [jobId, nonce]);
  // Route absent (before #121): offer the one Street View option main can serve, from the address.
  // Derived, so a changed address never re-asks the server.
  const options = useMemo(
    () => (raw?.kind === "unavailable" ? streetViewFallback(address) ?? raw : raw),
    [raw, address],
  );
  return { options, retry: () => setNonce((n) => n + 1) };
}

/**
 * EVERY PHOTO OPTION FOR THE SUBJECT, AND THE AGENT PICKS (owner spec, 2026-11-02).
 *
 * One radio group: each MLS photo carries its MLS number, Street View carries its capture date (or
 * says it is not stated). The default is marked and is what prints unless the agent chooses
 * another. Native radios, so Tab lands on the group and the arrow keys move within it.
 *
 * It never decides what prints: `onChoose` hands the chosen option to the flow, which turns it into
 * `meta.subject_photo` (steps) or saves it on the finished brief (report). A failure there is shown
 * here and the previous choice stays selected. Where the options are not available, this says so and
 * the upload control below it works exactly as before.
 */
export function SubjectPhotoPicker({
  options: optionsProp,
  chosenKey,
  uploadedInstead = false,
  onChoose,
  onRetry,
  onClear,
  where,
}: {
  options: SubjectPhotoOptions | null;
  /** The option the agent picked, or null while the default stands. */
  chosenKey: string | null;
  /** The agent uploaded their own photo, which prints instead of every option here. */
  uploadedInstead?: boolean;
  /** Receives the option and whether it is the default. May be async; a rejection is shown here. */
  onChoose: (photo: SubjectPhotoOption, isDefault: boolean) => void | Promise<void>;
  onRetry?: () => void;
  /** Takes the agent's pick back off when there is no default to return to. Omitted where that cannot be done. */
  onClear?: () => void;
  where: "review" | "report";
}) {
  let options = optionsProp;
  const id = useId();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The built-here Street View option cannot know the proxy will serve an image until it loads.
  const [fallbackFailed, setFallbackFailed] = useState(false);
  useEffect(() => { if (options?.kind === "ok" && options.fallback) setFallbackFailed(false); },
    [options?.kind === "ok" ? options.photos[0]?.url : null]);
  if (options?.kind === "ok" && options.fallback && fallbackFailed) options = { kind: "unavailable" };

  const heading = (
    <h3 id={`${id}-h`}>Photo options</h3>
  );

  if (options === null) {
    return (
      <section className="vb-photo-picker" data-testid="subject-photo-picker" aria-labelledby={`${id}-h`}>
        {heading}
        <p className="vb-photo-picker-note" role="status">Looking for MLS photos and Street View…</p>
      </section>
    );
  }
  if (options.kind === "unavailable") {
    return (
      <section className="vb-photo-picker" data-testid="subject-photo-picker" data-state="unavailable" aria-labelledby={`${id}-h`}>
        {heading}
        <p className="vb-photo-picker-note">
          Choosing between MLS photos and Street View is not available yet. The subject photo below
          works as before: upload one, or the photo read from your subject sheet prints.
        </p>
      </section>
    );
  }
  if (options.kind === "error") {
    return (
      <section className="vb-photo-picker" data-testid="subject-photo-picker" data-state="error" aria-labelledby={`${id}-h`}>
        {heading}
        <p className="vb-photo-picker-note" role="alert">
          The photo options could not be loaded. The subject photo below works as before.
        </p>
        {onRetry && <button type="button" className="btn" onClick={onRetry}>Try again</button>}
      </section>
    );
  }
  if (options.photos.length === 0) {
    return (
      <section className="vb-photo-picker" data-testid="subject-photo-picker" data-state="empty" aria-labelledby={`${id}-h`}>
        {heading}
        <p className="vb-photo-picker-note">
          No MLS photos or Street View image were found for this property. Upload a photo below, or
          the brief will say no photograph is included.
        </p>
      </section>
    );
  }

  const photos = options.photos;
  const def = defaultPhoto(photos);
  const defKey = def ? photoKey(def) : null;
  const selectedKey = uploadedInstead
    ? null
    : chosenKey && photos.some((p) => photoKey(p) === chosenKey) ? chosenKey : defKey;
  const selected = selectedKey ? photos.find((p) => photoKey(p) === selectedKey) ?? null : null;
  const hasMls = photos.some((p) => p.source === "mls");
  const canClear = !!onClear && !defKey && !!selectedKey && selectedKey === chosenKey && !busyKey;

  async function choose(p: SubjectPhotoOption) {
    const key = photoKey(p);
    if (key === selectedKey || busyKey) return;
    setBusyKey(key);
    setError(null);
    try {
      await onChoose(p, key === defKey);
    } catch (e) {
      setError(e instanceof Error ? e.message : "That photo could not be used. Choose another.");
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <section className="vb-photo-picker" data-testid="subject-photo-picker" data-state="ok" aria-labelledby={`${id}-h`}>
      {heading}
      <fieldset>
        <legend>
          {where === "report"
            ? "Choose the photo that prints on the first page. Changing it re-renders this brief from the analysis already run; no figure changes."
            : hasMls
              ? "Choose the photo that prints on the first page of the brief. The default is the first MLS photo, which may not show the front of the home."
              : `${options.fallback ? "MLS photos are not available yet." : "No MLS photo was found for this property."} Street View is listed, but it is never chosen for you: pick it yourself, or upload your own photo below.`}
        </legend>
        <ul className="vb-photo-picker-list">
          {photos.map((p) => {
            const key = photoKey(p);
            const { title, detail } = describePhoto(p);
            const isDefault = key === defKey;
            return (
              <li key={key}>
                <label className="vb-photo-opt" aria-busy={busyKey === key}>
                  <input
                    type="radio"
                    name={`${id}-photo`}
                    value={key}
                    checked={selectedKey === key}
                    onChange={() => void choose(p)}
                  />
                  <img src={p.url} alt={`${title}, ${detail}`} loading="lazy"
                    onError={options.fallback ? () => setFallbackFailed(true) : undefined} />
                  <span className="vb-photo-opt-title">{title}</span>
                  <span className="vb-photo-opt-detail">{detail}</span>
                  {isDefault && <span className="vb-photo-opt-tag">Default</span>}
                </label>
              </li>
            );
          })}
        </ul>
      </fieldset>
      {selected?.source === "streetview" && (
        <p className="vb-photo-picker-warn" role="status">{STREETVIEW_UNCREDITED}</p>
      )}
      {canClear && (
        <button type="button" className="btn" onClick={onClear}>Use none of these</button>
      )}
      {uploadedInstead && (
        <p className="vb-photo-picker-note">The photo you uploaded prints instead. Choose an option above to use it in its place.</p>
      )}
      {busyKey && <p className="vb-photo-picker-note" role="status">Preparing the photo…</p>}
      {error && <p className="vb-photo-picker-err" role="alert">{error}</p>}
    </section>
  );
}
